// src/components/TheShowPage.jsx
import React, { useState, useEffect } from "react";
import { 
  Play, 
  Eye, 
  Monitor, 
  Zap, 
  Globe, 
  Heart, 
  Shield, 
  AlertTriangle, 
  Trophy, 
  Calendar,
  Star,
  MessageSquare,
  Send,
  Volume2,
  Pause,
  SkipForward
} from "lucide-react";

export default function TheShowPage() {
  const [activeTab, setActiveTab] = useState("current-episode");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("12:34");
  const [blinkingEye, setBlinkingEye] = useState(true);
  
  // Simulate blinking eye animation
  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkingEye(false);
      setTimeout(() => setBlinkingEye(true), 150);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentEpisode = {
    title: "Episode #127: The Great E-commerce Catastrophe",
    airDate: "June 14, 2025",
    duration: "42:33",
    description: "Our AI hosts dive into the wild world of poorly designed shopping sites, featuring mysterious checkout flows and the legendary 'Add to Cart' button that leads nowhere.",
    featuredSites: [
      { name: "mysteryshopping.biz", score: 23, category: "E-commerce Horror" },
      { name: "checkout-nowhere.com", score: 31, category: "UX Nightmare" },
      { name: "loadingforever.shop", score: 18, category: "Performance Disaster" }
    ],
    highlights: [
      "🤖 ROB-07 discovers a 47-second loading animation",
      "🔍 SCAN-AI finds Comic Sans in the checkout process", 
      "🎯 LOGIC-BOT calculates -127% conversion rate probability"
    ]
  };

  const upcomingEpisodes = [
    {
      episode: 128,
      title: "Attack of the Auto-Playing Videos",
      date: "June 21, 2025",
      theme: "Media Mayhem"
    },
    {
      episode: 129,
      title: "The Cookie Banner Apocalypse",
      date: "June 28, 2025", 
      theme: "Privacy Paradox"
    },
    {
      episode: 130,
      title: "Web3 Wonderland or Wasteland?",
      date: "July 5, 2025",
      theme: "Crypto Critique"
    }
  ];

  const featuredSubmissions = [
    {
      id: 1,
      site: "flashbackfashion.net",
      submitter: "RetroReviewer99",
      category: "Retro Revival",
      score: 89,
      description: "Finally, someone who gets 90s web design right!",
      status: "featured"
    },
    {
      id: 2,
      site: "scamcentral.biz", 
      submitter: "WebWatchdog",
      category: "Scammer Alert",
      score: 12,
      description: "Fake crypto investment site with stolen testimonials",
      status: "investigating"
    },
    {
      id: 3,
      site: "helptheworld.org",
      submitter: "GoodCause2025",
      category: "Humanitarian",
      score: 94,
      description: "Clean water project with transparent donation tracking",
      status: "approved"
    }
  ];

  const TabButton = ({ id, label, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
        active 
          ? "bg-cyan-600 text-white border-b-2 border-cyan-400" 
          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900">
      

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-700">
          <TabButton
            id="current-episode"
            label="🎬 Current Episode"
            active={activeTab === "current-episode"}
            onClick={setActiveTab}
          />
          <TabButton
            id="submit-site"
            label="📝 Submit a Site"
            active={activeTab === "submit-site"}
            onClick={setActiveTab}
          />
          <TabButton
            id="best-of-internet"
            label="🏆 Best of Internet"
            active={activeTab === "best-of-internet"}
            onClick={setActiveTab}
          />
          <TabButton
            id="scammer-alerts"
            label="🚨 Scammer Alerts"
            active={activeTab === "scammer-alerts"}
            onClick={setActiveTab}
          />
          <TabButton
            id="humanitarian"
            label="❤️ Humanitarian"
            active={activeTab === "humanitarian"}
            onClick={setActiveTab}
          />
        </div>

        {/* Current Episode Tab */}
        {activeTab === "current-episode" && (
          <div className="space-y-8">
            {/* Episode Video Player */}
            <div className="bg-black/60 rounded-xl border border-cyan-500/30 overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-600/20 to-purple-600/20 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{currentEpisode.title}</h2>
                    <div className="flex items-center gap-4 text-gray-300 text-sm">
                      <span>📅 {currentEpisode.airDate}</span>
                      <span>⏱️ {currentEpisode.duration}</span>
                      <span className="px-2 py-1 bg-red-600 text-white rounded text-xs">🔴 LIVE</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="w-12 h-12 bg-cyan-600 hover:bg-cyan-700 rounded-full flex items-center justify-center transition-colors"
                    >
                      {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-1" />}
                    </button>
                    <Volume2 className="w-6 h-6 text-gray-400" />
                  </div>
                </div>
                
                <p className="text-gray-300 mb-4">{currentEpisode.description}</p>
                
                {/* Video Player Placeholder */}
                <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                  {/* Video Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-cyan-900/50"></div>
                  
                  {/* Retro Scanlines */}
                  <div className="absolute inset-0 opacity-20">
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className="w-full h-px bg-cyan-400/30" style={{top: `${i * 8.33}%`}}></div>
                    ))}
                  </div>
                  
                  {/* Main Eye Creature */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {/* Large Blinking Eye */}
                      <div className={`transition-all duration-200 mb-4 ${blinkingEye ? 'scale-100' : 'scale-y-10'}`}>
                        <Eye className="w-24 h-24 text-cyan-400 mx-auto" style={{
                          filter: 'drop-shadow(0 0 20px #22d3ee)'
                        }} />
                      </div>
                      
                      {/* AI Analysis Text */}
                      <div className="space-y-2 font-mono text-cyan-400">
                        <div className="text-lg animate-pulse">ANALYZING WEBSITE...</div>
                        <div className="text-sm opacity-75">
                          {isPlaying ? "PERFORMANCE SCORE: 23/100" : "CLICK PLAY TO START ANALYSIS"}
                        </div>
                      </div>
                      
                      {/* Animated Dots */}
                      <div className="flex justify-center gap-2 mt-4">
                        {[...Array(3)].map((_, i) => (
                          <div 
                            key={i} 
                            className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"
                            style={{animationDelay: `${i * 0.3}s`}}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Corner UI Elements */}
                  <div className="absolute top-4 left-4 font-mono text-cyan-400 text-sm opacity-75">
                    CODE_RATING_CODE v2.0
                  </div>
                  <div className="absolute top-4 right-4 font-mono text-cyan-400 text-sm opacity-75">
                    {isPlaying ? "● REC" : "⏸ PAUSED"}
                  </div>
                  
                  {/* Bottom Stats */}
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between font-mono text-xs text-cyan-400 opacity-75">
                    <span>SITES ANALYZED: 1,247,382</span>
                    <span>AI CONFIDENCE: 98.7%</span>
                  </div>
                  
                  {/* Retro Computer Border Effect */}
                  <div className="absolute inset-2 border border-cyan-400/30 rounded pointer-events-none"></div>
                </div>
                
                {/* Mock Player Controls */}
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-cyan-400 font-mono text-sm">{currentTime}</span>
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="w-1/3 h-full bg-gradient-to-r from-cyan-500 to-purple-500"></div>
                    </div>
                    <span className="text-gray-400 font-mono text-sm">{currentEpisode.duration}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SkipForward className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-400">Skip to next site review</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>1,247 viewers</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Episode Highlights */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">🤖 AI Host Highlights</h3>
                <div className="space-y-3">
                  {currentEpisode.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                      <span className="text-lg">{highlight.split(' ')[0]}</span>
                      <span className="text-gray-300">{highlight.substring(highlight.indexOf(' ') + 1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Featured Sites This Episode */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">🎯 Featured Sites This Episode</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {currentEpisode.featuredSites.map((site, index) => (
                  <div key={index} className="bg-black/40 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{site.name}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        site.score < 30 ? 'bg-red-600 text-white' : 
                        site.score < 60 ? 'bg-yellow-600 text-white' : 
                        'bg-green-600 text-white'
                      }`}>
                        {site.score}
                      </span>
                    </div>
                    <span className="text-gray-400 text-sm">{site.category}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Episodes */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">📺 Upcoming Episodes</h3>
              <div className="space-y-3">
                {upcomingEpisodes.map((episode) => (
                  <div key={episode.episode} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-gray-700">
                    <div>
                      <h4 className="text-white font-medium">Episode #{episode.episode}: {episode.title}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                        <span>📅 {episode.date}</span>
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded">{episode.theme}</span>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors text-sm">
                      Set Reminder
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit a Site Tab */}
        {activeTab === "submit-site" && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Submit a Website for Review</h2>
                <p className="text-gray-300">Help us log the entire Internet A-Z! Our AI hosts are always looking for new sites to review.</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">Website URL *</label>
                  <input
                    type="url"
                    placeholder="https://example.com"
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Category</label>
                  <select className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none">
                    <option value="">Select a category...</option>
                    <option value="best-of-internet">🏆 Best of Internet</option>
                    <option value="design-disaster">💥 Design Disaster</option>
                    <option value="performance-problem">⚡ Performance Problem</option>
                    <option value="ux-nightmare">😱 UX Nightmare</option>
                    <option value="scammer-alert">🚨 Scammer Alert</option>
                    <option value="web3-wonder">🔗 Web3 Project</option>
                    <option value="humanitarian">❤️ Humanitarian Project</option>
                    <option value="retro-revival">📼 Retro Revival</option>
                    <option value="hidden-gem">💎 Hidden Gem</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Why should we review this site?</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us what makes this site special, terrible, or interesting..."
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none resize-none"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Your Name/Handle (optional)</label>
                  <input
                    type="text"
                    placeholder="How should we credit you on the show?"
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium text-sm">Submission Guidelines</p>
                      <ul className="text-yellow-300 text-sm mt-2 space-y-1">
                        <li>• No illegal or harmful content</li>
                        <li>• For scammer alerts, provide evidence</li>
                        <li>• Submissions may be featured on live show</li>
                        <li>• Our AI will analyze within 24-48 hours</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all">
                  <Send className="w-5 h-5 inline mr-2" />
                  Submit for AI Review
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Best of Internet Tab */}
        {activeTab === "best-of-internet" && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">🏆 Best of the Internet</h2>
              <p className="text-gray-300">Sites that made our AI hosts actually impressed (rare achievement!)</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredSubmissions.filter(s => s.category === "Retro Revival" || s.category === "Humanitarian" || s.score > 85).map((site) => (
                <div key={site.id} className="bg-gradient-to-br from-green-500/10 to-blue-500/10 backdrop-blur-sm rounded-xl border border-green-500/30 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold">{site.site}</h3>
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      <span className="bg-green-600 text-white px-2 py-1 rounded text-sm font-bold">{site.score}</span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">{site.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-400">{site.category}</span>
                    <span className="text-gray-400">by {site.submitter}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scammer Alerts Tab */}
        {activeTab === "scammer-alerts" && (
          <div className="space-y-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-red-500" />
                <h2 className="text-2xl font-bold text-white">🚨 Scammer Alert Network</h2>
              </div>
              <p className="text-gray-300 mb-4">
                Help protect the internet! Report suspicious websites and we'll have our AI investigate.
              </p>
              <div className="bg-red-500/20 rounded-lg p-4">
                <p className="text-red-300 text-sm">
                  <strong>⚠️ Warning:</strong> Never enter personal information or make payments on suspicious sites. 
                  If you've been scammed, contact your bank and local authorities immediately.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredSubmissions.filter(s => s.category === "Scammer Alert" || s.score < 30).map((site) => (
                <div key={site.id} className="bg-red-500/5 backdrop-blur-sm rounded-xl border border-red-500/30 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold">{site.site}</h3>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold">{site.score}</span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">{site.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-400">Status: {site.status}</span>
                    <span className="text-gray-400">Reported by {site.submitter}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Humanitarian Tab */}
        {activeTab === "humanitarian" && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">❤️ Humanitarian Projects</h2>
              <p className="text-gray-300">Websites making the world a better place, one pixel at a time</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredSubmissions.filter(s => s.category === "Humanitarian").map((site) => (
                <div key={site.id} className="bg-gradient-to-br from-pink-500/10 to-red-500/10 backdrop-blur-sm rounded-xl border border-pink-500/30 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold">{site.site}</h3>
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" />
                      <span className="bg-green-600 text-white px-2 py-1 rounded text-sm font-bold">{site.score}</span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">{site.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-pink-400 text-xs">{site.category}</span>
                    <button className="px-3 py-1 bg-pink-600 hover:bg-pink-700 text-white rounded text-sm transition-colors">
                      Visit Site
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-xl border border-pink-500/30 p-6">
              <h3 className="text-lg font-semibold text-white mb-3">🌟 Spotlight: Tech for Good</h3>
              <p className="text-gray-300 text-sm mb-4">
                This week we're featuring projects that use technology to solve real world problems. 
                Our AI hosts are particularly impressed by transparent donation tracking and user-focused design.
              </p>
              <button className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded transition-colors">
                Submit Your Project
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}